import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import type { JsonValue } from "../../api/canonicalLineage";
import {
  appendJournalEntry,
  journalEntryWriteErrorMessage,
  materializeStructuredJournalContent,
  type AppendJournalEntryRequest,
  type JournalEntryWriteCapability,
  type JournalEntryWriteResult,
} from "../../api/journalEntries";
import type { TemporalSnapshotCut } from "./temporalState";
import "./human-provenance.css";

export interface JournalDecisionOption {
  id: string;
  label: string;
  status: string;
}

export interface JournalSensitivityTierOption {
  id: string;
  label: string;
}

export interface JournalEntrySubject {
  artifactVersionId: string;
  label: string;
  sensitivityTierId: string;
  sensitivityTierName: string;
}

export interface JournalEntryFormProps {
  projectId: string;
  subject: JournalEntrySubject;
  cut: TemporalSnapshotCut;
  capability: JournalEntryWriteCapability;
  decisions: readonly JournalDecisionOption[];
  sensitivityTiers: readonly JournalSensitivityTierOption[];
  onClose: () => void;
  onPublished: (result: JournalEntryWriteResult) => void;
  append?: typeof appendJournalEntry;
  createId?: () => string;
}

interface ReceiptBinding {
  fingerprint: string;
  key: string;
}

function browserUuid(): string {
  if (typeof globalThis.crypto?.randomUUID !== "function") {
    throw new Error("Secure browser UUID generation is required for journal publication.");
  }
  return globalThis.crypto.randomUUID();
}

function parseStructuredContent(value: string): JsonValue {
  if (!value.trim()) throw new Error("Structured JSON content is required.");
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (error) {
    const detail = error instanceof Error ? error.message : "invalid JSON";
    throw new Error(`Structured content is not valid JSON: ${detail}`);
  }
  if (parsed === null) throw new Error("Structured JSON content cannot be null.");
  return parsed as JsonValue;
}

function humanize(value: string): string {
  return value.split("_").join(" ");
}

/** Accessible authoring surface for one immutable, exact-subject journal command. */
export function JournalEntryForm({
  projectId,
  subject,
  cut,
  capability,
  decisions,
  sensitivityTiers,
  onClose,
  onPublished,
  append = appendJournalEntry,
  createId = browserUuid,
}: JournalEntryFormProps) {
  const headingId = useId();
  const entryTypeId = useId();
  const effectiveAtId = useId();
  const decisionId = useId();
  const sensitivityId = useId();
  const contentNameId = useId();
  const structuredContentId = useId();
  const entryTypeRef = useRef<HTMLInputElement | null>(null);
  const [logicalId] = useState(() => createId());
  const [entryType, setEntryType] = useState("review_note");
  const [effectiveAt, setEffectiveAt] = useState(cut.effectiveAsOf);
  const [selectedDecisionId, setSelectedDecisionId] = useState("");
  const [sensitivityTierId, setSensitivityTierId] = useState("");
  const [contentName, setContentName] = useState(`Journal · ${subject.label}`.slice(0, 300));
  const [structuredContent, setStructuredContent] = useState("");
  const [receiptBinding, setReceiptBinding] = useState<ReceiptBinding | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => entryTypeRef.current?.focus(), []);
  useEffect(() => {
    setSelectedDecisionId((current) => (
      current === "" || decisions.some((decision) => decision.id === current) ? current : ""
    ));
  }, [decisions]);
  useEffect(() => {
    setSensitivityTierId((current) => (
      current === "" || sensitivityTiers.some((tier) => tier.id === current) ? current : ""
    ));
  }, [sensitivityTiers]);

  const resetPublicationAttempt = () => {
    setReceiptBinding(null);
    setError(null);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || !capability.available) return;
    setError(null);
    setSubmitting(true);
    try {
      const content = await materializeStructuredJournalContent({
        logicalId,
        name: contentName,
        structuredContent: parseStructuredContent(structuredContent),
      });
      const commandWithoutReceipt = {
        project_id: projectId,
        subject_artifact_version_id: subject.artifactVersionId,
        decision_id: selectedDecisionId || null,
        entry_type: entryType,
        effective_at: effectiveAt,
        sensitivity_tier_id: sensitivityTierId || null,
        content,
      };
      const fingerprint = JSON.stringify(commandWithoutReceipt);
      const key = receiptBinding?.fingerprint === fingerprint
        ? receiptBinding.key
        : `journal-ui-${createId()}`;
      setReceiptBinding({ fingerprint, key });
      const command: AppendJournalEntryRequest = {
        ...commandWithoutReceipt,
        idempotency_key: key,
      };
      const response = await append(command);
      onPublished(response.data);
    } catch (caught) {
      setError(journalEntryWriteErrorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape" || submitting) return;
    event.stopPropagation();
    onClose();
  };

  return (
    <aside
      className="human-provenance__journal-form"
      aria-labelledby={headingId}
      data-testid="journal-entry-form"
      onKeyDown={onKeyDown}
    >
      <header>
        <div>
          <span>Immutable human provenance</span>
          <h2 id={headingId}>Publish journal entry</h2>
        </div>
        <button type="button" onClick={onClose} disabled={submitting} aria-label="Close journal entry form">×</button>
      </header>

      <section className="human-provenance__journal-subject" aria-label="Exact journal subject">
        <span>Subject ArtifactVersion</span>
        <strong>{subject.label}</strong>
        <code>{subject.artifactVersionId}</code>
        <small>
          Readable at the settled cut <time dateTime={cut.effectiveAsOf}>{cut.effectiveAsOf}</time>.
          The server rechecks visibility when publishing.
        </small>
      </section>

      <form onSubmit={submit} noValidate>
        <fieldset disabled={submitting}>
          <legend>Journal semantics</legend>
          <label htmlFor={entryTypeId}>
            <span>Entry type</span>
            <input
              ref={entryTypeRef}
              id={entryTypeId}
              value={entryType}
              required
              minLength={1}
              maxLength={100}
              pattern="[a-z][a-z0-9_.-]{0,99}"
              autoComplete="off"
              aria-describedby={`${entryTypeId}-hint`}
              onChange={(event) => setEntryType(event.currentTarget.value)}
            />
            <small id={`${entryTypeId}-hint`}>Lowercase identifier, for example <code>review_note</code>.</small>
          </label>
          <label htmlFor={effectiveAtId}>
            <span>Effective / event time</span>
            <input
              id={effectiveAtId}
              value={effectiveAt}
              required
              autoComplete="off"
              spellCheck={false}
              aria-describedby={`${effectiveAtId}-hint`}
              onChange={(event) => setEffectiveAt(event.currentTarget.value)}
            />
            <small id={`${effectiveAtId}-hint`}>RFC 3339 with a timezone. Backdated entries retain their publication recording time.</small>
          </label>
          <label htmlFor={decisionId}>
            <span>Visible decision <em>optional</em></span>
            <select
              id={decisionId}
              value={selectedDecisionId}
              onChange={(event) => setSelectedDecisionId(event.currentTarget.value)}
            >
              <option value="">No decision link</option>
              {decisions.map((decision) => (
                <option key={decision.id} value={decision.id}>
                  {decision.label} · {humanize(decision.status)}
                </option>
              ))}
            </select>
            <small>Only decision records disclosed by human.v1 for this exact subject are offered.</small>
          </label>
          <label htmlFor={sensitivityId}>
            <span>Content sensitivity tier</span>
            <select
              id={sensitivityId}
              value={sensitivityTierId}
              onChange={(event) => setSensitivityTierId(event.currentTarget.value)}
            >
              <option value="">Automatic minimum permitted tier</option>
              {sensitivityTiers.map((tier) => (
                <option key={tier.id} value={tier.id}>{tier.label}</option>
              ))}
            </select>
            <small>
              When omitted, the server applies the minimum tier required by the subject and optional decision.
              An explicit tier must meet or exceed that floor.
            </small>
          </label>
        </fieldset>

        <fieldset disabled={submitting}>
          <legend>Immutable content ArtifactVersion</legend>
          <label htmlFor={contentNameId}>
            <span>Content artifact name</span>
            <input
              id={contentNameId}
              value={contentName}
              required
              maxLength={300}
              autoComplete="off"
              onChange={(event) => setContentName(event.currentTarget.value)}
            />
          </label>
          <label htmlFor={structuredContentId}>
            <span>Structured JSON content</span>
            <textarea
              id={structuredContentId}
              value={structuredContent}
              required
              rows={10}
              spellCheck={false}
              placeholder={'{\n  "summary": "Exact review finding"\n}'}
              aria-describedby={`${structuredContentId}-hint`}
              onChange={(event) => setStructuredContent(event.currentTarget.value)}
            />
            <small id={`${structuredContentId}-hint`}>
              Non-null JSON only. RFC 8785 bytes and SHA-256 identity are computed before the dedicated command is sent.
            </small>
          </label>
        </fieldset>

        <details>
          <summary>Publication identity</summary>
          <dl>
            <div><dt>Content logical ID</dt><dd><code>{logicalId}</code></dd></div>
            <div><dt>Idempotency key</dt><dd><code>{receiptBinding?.key ?? "Assigned on first publication attempt"}</code></dd></div>
            <div><dt>Raw HTTP limit</dt><dd>{capability.limits.maximumRequestBodyBytes.toLocaleString()} bytes</dd></div>
          </dl>
        </details>

        {error ? (
          <div className="human-provenance__journal-error" role="alert">
            <p>{error}</p>
            {error.includes("new publication attempt") ? (
              <button type="button" onClick={resetPublicationAttempt}>Start a new publication attempt</button>
            ) : null}
          </div>
        ) : null}

        <footer>
          <button type="button" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" disabled={submitting || !capability.available}>
            {submitting ? "Publishing immutable entry…" : "Publish journal entry"}
          </button>
        </footer>
      </form>
    </aside>
  );
}
